import type { Component } from 'vue';
import type { FormatterFactory, FormatterSpec, GridFormatter } from '../types';

const factories = new Map<string, FormatterFactory>();

export function registerFormatter(
  name: string,
  factory: FormatterFactory,
): void {
  if (factories.has(name)) {
    throw new Error(`grid formatter already registered: "${name}"`);
  }
  factories.set(name, factory);
}

export function getFormatterFactory(name: string): FormatterFactory {
  const factory = factories.get(name);
  if (!factory) {
    throw new TypeError(`unknown grid formatter: "${name}"`);
  }
  return factory;
}

export function hasFormatter(name: string): boolean {
  return factories.has(name);
}

function isComponent(spec: object): spec is Component {
  return (
    'render' in spec ||
    'setup' in spec ||
    'template' in spec ||
    '__vccOpts' in spec ||
    'ssrRender' in spec
  );
}

export function resolveFormatter(
  spec: FormatterSpec | null | undefined,
): GridFormatter | null {
  if (spec == null) {
    return null;
  }
  if (Array.isArray(spec)) {
    return getFormatterFactory('chain')({ formatters: spec });
  }
  if (typeof spec === 'string') {
    return getFormatterFactory(spec)();
  }
  if (typeof spec === 'function') {
    return { component: spec as Component };
  }
  if (typeof spec === 'object') {
    if (isComponent(spec)) {
      return { component: spec };
    }
    if (typeof (spec as { name?: unknown }).name === 'string') {
      const { name, ...options } = spec as { name: string } & Record<
        string,
        any
      >;
      return getFormatterFactory(name)(options);
    }
  }
  throw new TypeError(
    'invalid formatter: expected a name, a { name, ...options } object, or a component',
  );
}
